import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import CoursePricingPanel, { useCourseOffers } from './CoursePricingPanel';
import { formatPrice, lowestPrice, type IPricingSelection } from '../../utils/coursePricing';
import type { ICourse } from '../../utils/courseSchedule';
import type { CourseMemberState } from './CourseRow';

interface CoursePricePopoverProps {
  course: ICourse;
  /** The currently selected member, or null when none chosen / logged out. */
  selectedMemberId?: number | null;
  /** This member's enrollment state for this course. */
  state?: CourseMemberState;
  /** Whether the course's active season is full (buy actions are disabled). */
  isFull?: boolean;
  /** True while a join/choose action is in flight for this course. */
  submitting?: boolean;
  /**
   * Bridge into the sign-up / choose-payment flow. When provided together with a
   * selected member who is not yet enrolled/pending, the modal shows a "Sign up"
   * CTA. Omit to keep the modal read-only (public / logged-out browsing).
   *
   * `selection` is present when the member picked a season stretch, so the
   * caller can enrol and assign that stretch in one step.
   */
  onChoose?: (course: ICourse, selection?: IPricingSelection) => void;
}

/**
 * Compact "from X CZK" price trigger for a course (kroužek) poster card, opening
 * the full pricing breakdown in a modal. Renders nothing when the course has no
 * priced offer.
 *
 * Only the trigger and the modal live here — the content is `CoursePricingPanel`,
 * shared with the inline collapsible pricing section in CourseDetail so the two
 * surfaces cannot drift. The trigger stops click propagation so it never bubbles
 * to a parent card click (e.g. opening the tile detail).
 */
const CoursePricePopover: React.FC<CoursePricePopoverProps> = ({
  course,
  selectedMemberId = null,
  state = 'none',
  isFull = false,
  submitting = false,
  onChoose,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [isOpen, setIsOpen] = useState(false);

  // Gate the trigger on the same derivation the panel uses, so we never show a
  // price badge that opens an empty modal.
  const { offers } = useCourseOffers(course, t, locale);
  if (offers.length === 0) return null;

  const fromLabel = formatPrice(lowestPrice(course), locale);

  return (
    <>
      <button
        type="button"
        className="course-price-popover__trigger"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
        aria-label={t('courses.viewPricing')}
      >
        {fromLabel ? t('courses.priceFrom', { price: fromLabel }) : t('courses.viewPricing')}
      </button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('courses.pricing')}
        /* Season tiers lay segments out side by side; the 400px "small" modal
           squeezed four quarters into unreadable slivers on any screen. */
        size="medium"
      >
        <CoursePricingPanel
          course={course}
          selectedMemberId={selectedMemberId}
          state={state}
          isFull={isFull}
          submitting={submitting}
          onChoose={onChoose}
          onAfterChoose={() => setIsOpen(false)}
        />
      </Modal>
    </>
  );
};

export default CoursePricePopover;
